import * as React from "react"
import { trackActivity } from "./activity"
import { DELIVERY_CONTACT_REQUIRED } from "../../../supabase/functions/_shared/delivery-coverage.ts"
import { useAddressLookup } from "./hooks/useAddressLookup"
import { useBillingAddress } from "./hooks/useBillingAddress"
import { useBookingAvailability } from "./hooks/useBookingAvailability"
import { useBookingDraft } from "./hooks/useBookingDraft"
import { useBookingSelection } from "./hooks/useBookingSelection"
import { useBrowserSession } from "./hooks/useBrowserSession"
import { useCalendarAvailability } from "./hooks/useCalendarAvailability"
import { useCheckoutAnalytics } from "./hooks/useCheckoutAnalytics"
import { useCheckoutLifecycle } from "./hooks/useCheckoutLifecycle"
import { useCheckoutModel } from "./hooks/useCheckoutModel"
import { useCheckoutNavigation } from "./hooks/useCheckoutNavigation"
import { useCheckoutPayment } from "./hooks/useCheckoutPayment"
import { useCheckoutProduct } from "./hooks/useCheckoutProduct"
import { useCheckoutSession } from "./hooks/useCheckoutSession"
import { useCheckoutState } from "./hooks/useCheckoutState"
import { useCustomerDetails } from "./hooks/useCustomerDetails"
import { useStripeCheckout } from "./hooks/useStripeCheckout"
import type { HireBookingProps } from "./types"

export function useCheckoutController(props: HireBookingProps) {
    // Preserve hook/effect order so draft restoration, availability and Stripe cleanup stay coordinated.
    const state = useCheckoutState()
    const model = useCheckoutModel({ props, ...state })
    const navigation = useCheckoutNavigation({ ...state, ...model })
    const tracking = useCheckoutAnalytics({ ...model, ...state })
    const browser = useBrowserSession()
    useCheckoutProduct({ props, ...state, ...model, ...tracking })
    const lifecycle = useCheckoutLifecycle({ ...state, ...model })
    const calendar = useCalendarAvailability({ ...state, ...model, ...browser, ...lifecycle, ...navigation })
    const selection = useBookingSelection({ ...state, ...model, ...calendar, ...lifecycle, ...tracking, ...browser })
    useBookingAvailability({ ...state, ...model, ...lifecycle, ...selection, ...tracking })
    const session = useCheckoutSession({ ...state, ...model, ...browser, ...navigation, ...tracking, ...lifecycle, ...selection, props })
    useBookingDraft({ ...state, ...model, ...navigation, pollStatus: session.pollStatus })
    useStripeCheckout({ ...state, props })
    useBillingAddress(state)
    useAddressLookup({ ...state, ...model, ...browser })
    const details = useCustomerDetails({ ...state, ...model, props, ...navigation })
    const payment = useCheckoutPayment({ ...state, ...model, ...details, ...session })

    const lastContactCheck = React.useRef("")
    React.useEffect(() => {
        if (!model.deliveryContactRequired) { lastContactCheck.current = ""; return }
        if (["paying", "confirming", "complete"].includes(state.phase)) return
        state.setCalendarOpen(false)
        if (state.draftRestored && (state.journeyStep !== "booking" || state.bookingPane !== "selection")) {
            navigation.navigateWorkflow("dates", "replace")
        }
        // Match the existing postcode debounce; this outcome carries no postcode or customer details.
        if (state.postcode !== state.settledPostcode) return
        const key = `${state.activeSlug}:${model.normalizedPostcode}`
        if (lastContactCheck.current === key) return
        lastContactCheck.current = key
        trackActivity("availability_checked", { result: "contact_required", code: DELIVERY_CONTACT_REQUIRED, quantity: state.quantity })
    }, [model.deliveryContactRequired, model.normalizedPostcode, state.postcode, state.settledPostcode,
        state.activeSlug, state.phase, state.draftRestored, state.journeyStep, state.bookingPane, state.quantity, navigation.navigateWorkflow])

    const editBooking = () => navigation.navigateWorkflow("dates")
    return {
        navigateWorkflow: navigation.navigateWorkflow,
        lastMonth: model.lastMonth,
        unavailableDates: model.unavailableDates,
        unavailableServiceDates: model.unavailableServiceDates,
        activeDateField: state.activeDateField,
        range: state.range,
        product: state.product,
        calendarSnapshot: state.calendarSnapshot,
        today: model.today,
        isSelectedStartDate: selection.isSelectedStartDate,
        calendarReady: model.calendarReady,
        deliveryContactRequired: model.deliveryContactRequired,
        calendarRefreshing: model.calendarRefreshing,
        deliveryFeePence: model.deliveryFeePence,
        resetDates: selection.resetDates,
        chooseDay: selection.chooseDay,
        confirmDateSelection: selection.confirmDateSelection,
        firstMonth: model.firstMonth,
        isMobile: state.isMobile,
        choice: model.choice,
        calendarRangeError: model.calendarRangeError,
        phase: state.phase,
        availability: state.availability,
        stripeReady: state.stripeReady,
        clientSecret: state.clientSecret,
        paymentComplete: payment.paymentComplete,
        detailsComplete: details.detailsComplete,
        addressLookupPhase: state.addressLookupPhase,
        manualDeliveryEntry: state.manualDeliveryEntry,
        stripeCanConfirm: state.stripeCanConfirm,
        walletAvailable: state.walletAvailable,
        quote: state.quote,
        quantity: state.quantity,
        addonTotalPence: model.addonTotalPence,
        currentDeliveryAddress: details.currentDeliveryAddress,
        selectedAddonIds: state.selectedAddonIds,
        journeyStep: state.journeyStep,
        bookingPane: state.bookingPane,
        continueFromSelection: session.continueFromSelection,
        continueFromAddons: session.continueFromAddons,
        confirmPayment: payment.confirmPayment,
        restoredCheckoutPhase: state.restoredCheckoutPhase,
        setRestoredCheckoutRetry: state.setRestoredCheckoutRetry,
        calendarPhase: state.calendarPhase,
        calendarError: state.calendarError,
        refreshCalendar: calendar.refreshCalendar,
        instanceId: state.instanceId,
        props,
        dateNotice: state.dateNotice,
        activeSlug: state.activeSlug,
        meta: model.meta,
        error: state.error,
        setProductRetry: state.setProductRetry,
        calendarOpen: state.calendarOpen,
        previewDates: model.previewDates,
        postcode: state.postcode,
        customer: state.customer,
        differentBilling: state.differentBilling,
        billingComplete: state.billingComplete,
        billingAddress: state.billingAddress,
        changeQuantity: selection.changeQuantity,
        stockCap: model.stockCap,
        setPostcode: state.setPostcode,
        clearBookingResult: selection.clearBookingResult,
        normalizedPostcode: model.normalizedPostcode,
        openDatePicker: selection.openDatePicker,
        dateMessage: model.dateMessage,
        toggleAddon: session.toggleAddon,
        analytics: tracking.analytics,
        detailsPanelRef: state.detailsPanelRef,
        detailErrors: state.detailErrors,
        validationAttempt: state.validationAttempt,
        setCustomer: state.setCustomer,
        clearDetailError: details.clearDetailError,
        addressOptions: state.addressOptions,
        selectedAddressId: state.selectedAddressId,
        setSelectedAddressId: state.setSelectedAddressId,
        setSelectedDeliveryAddress: state.setSelectedDeliveryAddress,
        setManualDeliveryEntry: state.setManualDeliveryEntry,
        manualDeliveryAddress: state.manualDeliveryAddress,
        setManualDeliveryAddress: state.setManualDeliveryAddress,
        addressLookupMessage: state.addressLookupMessage,
        setDifferentBilling: state.setDifferentBilling,
        setBillingComplete: state.setBillingComplete,
        setBillingAddress: state.setBillingAddress,
        billingMountRef: state.billingMountRef,
        expressMountRef: state.expressMountRef,
        paymentMountRef: state.paymentMountRef,
        selectedAddons: model.selectedAddons,
        bookingStatus: state.bookingStatus,
        confirmationChecking: session.confirmationChecking,
        confirmationError: session.confirmationError,
        retryConfirmation: () => session.refreshConfirmation({ confirmationToken: state.confirmationToken }),
        setCalendarOpen: state.setCalendarOpen,
        editBooking,
    }
}

export type CheckoutController = ReturnType<typeof useCheckoutController>
